import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  CalendarDays,
  Dog,
  FileText,
  LayoutDashboard,
  Lock,
  ShieldCheck,
  Users,
} from 'lucide-react';
import clsx from 'clsx';

/** Beispieldaten der Vorschau - rein illustrativ, die Texte kommen aus landing.json (preview.*). */
const PREGNANCY_DAY = 42;
const PREGNANCY_TOTAL = 63;
const HEAT_IN_DAYS = 12;
const PUPPY_COUNT = 6;
/** Durchschnittsgewicht des Wurfs in Gramm ueber die ersten drei Wochen (Sparkline). */
const LITTER_WEIGHTS = [410, 470, 560, 640, 750, 860, 990, 1120, 1260];

const SIDEBAR_ICONS = [LayoutDashboard, Dog, CalendarDays, Users, FileText];

const APPOINTMENTS = [
  { dayKey: 'mon', titleKey: 'appointment1', dot: 'bg-accent-400' },
  { dayKey: 'thu', titleKey: 'appointment2', dot: 'bg-secondary-400' },
  { dayKey: 'sat', titleKey: 'appointment3', dot: 'bg-primary-400' },
] as const;

function sparklinePath(values: number[], width: number, height: number) {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const step = width / (values.length - 1);
  return values
    .map((v, i) => {
      const x = i * step;
      const y = height - ((v - min) / (max - min)) * (height - 4) - 2;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

const Panel: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <div className={clsx('rounded-xl border border-dark-700/70 bg-dark-800/50 p-3.5', className)}>{children}</div>
);

const PanelLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[11px] font-medium uppercase tracking-wider text-dark-400">{children}</p>
);

/** Stilisierte App-Ansicht fuer den Hero der Startseite. Rein dekorativ (aria-hidden), zeigt
 * aber echte canebase-Bereiche, damit Besucher:innen sofort sehen, womit sie es zu tun haben. */
export const HeroPreview: React.FC = () => {
  const { t } = useTranslation();
  const progress = Math.round((PREGNANCY_DAY / PREGNANCY_TOTAL) * 100);
  const line = sparklinePath(LITTER_WEIGHTS, 120, 40);

  return (
    <div className="relative mx-auto w-full max-w-xl lg:max-w-none" aria-hidden="true">
      {/* Weicher Lichtschein hinter dem Fenster */}
      <div className="deco-glow pointer-events-none absolute -inset-x-10 -inset-y-12 rounded-full bg-gradient-to-tr from-primary-500/25 via-secondary-500/10 to-transparent blur-3xl" />

      <div className="landing-preview-shadow relative overflow-hidden rounded-2xl border border-dark-700/80 bg-dark-900/95">
        {/* Fensterleiste */}
        <div className="flex h-10 items-center gap-2 border-b border-dark-700/70 bg-dark-800/60 px-4">
          <span className="h-2.5 w-2.5 rounded-full bg-dark-600" />
          <span className="h-2.5 w-2.5 rounded-full bg-dark-600" />
          <span className="h-2.5 w-2.5 rounded-full bg-dark-600" />
          <span className="mx-auto inline-flex items-center gap-1.5 rounded-md bg-dark-900/70 px-3 py-1 text-[11px] text-dark-400">
            <Lock size={10} />
            canebase.ch
          </span>
          <span className="w-12" />
        </div>

        <div className="flex">
          {/* Schmale Navigationsleiste */}
          <div className="hidden w-14 shrink-0 flex-col items-center gap-2 border-r border-dark-700/70 py-4 sm:flex">
            <img src="/canebase-icon.png" alt="" className="mb-2 h-6 w-auto object-contain" />
            {SIDEBAR_ICONS.map((Icon, i) => (
              <span
                key={i}
                className={clsx(
                  'flex h-8 w-8 items-center justify-center rounded-lg',
                  i === 0 ? 'bg-primary-500/15 text-primary-300' : 'text-dark-500'
                )}
              >
                <Icon size={16} />
              </span>
            ))}
          </div>

          <div className="min-w-0 flex-1 space-y-3 p-4 sm:p-5 xl:pb-12">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] text-dark-400">{t('landing:preview.kennel')}</p>
                <p className="text-base font-semibold text-dark-50">{t('landing:preview.title')}</p>
              </div>
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 text-xs font-semibold text-white">
                AK
              </span>
            </div>

            {/* Traechtigkeit */}
            <Panel>
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-500/15 text-sm font-semibold text-accent-300">
                    L
                  </span>
                  <div className="min-w-0">
                    <PanelLabel>{t('landing:preview.pregnancy')}</PanelLabel>
                    <p className="truncate text-sm font-semibold text-dark-50">Luna</p>
                  </div>
                </div>
                <p className="shrink-0 text-sm font-semibold text-dark-100">
                  {t('landing:preview.pregnancyDay', { day: PREGNANCY_DAY, total: PREGNANCY_TOTAL })}
                </p>
              </div>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-dark-700">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-primary-500 to-secondary-400"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-dark-400">
                {t('landing:preview.pregnancyDue', { days: PREGNANCY_TOTAL - PREGNANCY_DAY })}
              </p>
            </Panel>

            <div className="grid grid-cols-2 gap-3">
              {/* Naechste Laeufigkeit */}
              <Panel>
                <PanelLabel>{t('landing:preview.heat')}</PanelLabel>
                <p className="mt-1.5 text-sm font-semibold text-dark-50">Nala</p>
                <p className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-dark-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent-400" />
                  {t('landing:preview.heatIn', { days: HEAT_IN_DAYS })}
                </p>
              </Panel>

              {/* Wurf mit Gewichtskurve */}
              <Panel>
                <PanelLabel>{t('landing:preview.litter')}</PanelLabel>
                <p className="mt-1.5 text-sm font-semibold text-dark-50">
                  {t('landing:preview.puppies', { n: PUPPY_COUNT })}
                </p>
                <svg viewBox="0 0 120 40" className="mt-1 h-8 w-full" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="landing-spark" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgb(var(--color-secondary-400))" stopOpacity="0.35" />
                      <stop offset="100%" stopColor="rgb(var(--color-secondary-400))" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d={`${line} L120,40 L0,40 Z`} fill="url(#landing-spark)" />
                  <path
                    d={line}
                    fill="none"
                    stroke="rgb(var(--color-secondary-400))"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
              </Panel>
            </div>

            {/* Termine */}
            <Panel className="space-y-2.5">
              <PanelLabel>{t('landing:preview.appointments')}</PanelLabel>
              {APPOINTMENTS.map((a) => (
                <div key={a.titleKey} className="flex items-center gap-3">
                  <span className="w-9 shrink-0 rounded-md bg-dark-900/80 py-1 text-center text-[10px] font-semibold uppercase text-dark-300">
                    {t(`landing:preview.days.${a.dayKey}`)}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-xs text-dark-200">
                    {t(`landing:preview.${a.titleKey}`)}
                  </span>
                  <span className={clsx('h-2 w-2 shrink-0 rounded-full', a.dot)} />
                </div>
              ))}
            </Panel>
          </div>
        </div>
      </div>

      {/* Schwebende Hinweise - nur mit genug Platz daneben */}
      <div className="absolute -bottom-6 -left-8 hidden items-center gap-3 rounded-xl border border-dark-700/80 bg-dark-800/95 px-4 py-3 shadow-xl backdrop-blur xl:flex">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-green-500/15 text-green-400">
          <ShieldCheck size={18} />
        </span>
        <div>
          <p className="text-xs font-semibold text-dark-50">{t('landing:preview.checkTitle')}</p>
          <p className="text-[11px] text-dark-400">{t('landing:preview.checkResult')}</p>
        </div>
      </div>

      <div className="absolute -right-5 -top-5 hidden items-center gap-3 rounded-xl border border-dark-700/80 bg-dark-800/95 px-4 py-3 shadow-xl backdrop-blur xl:flex">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500/15 text-primary-300">
          <Bell size={18} />
        </span>
        <div>
          <p className="text-xs font-semibold text-dark-50">{t('landing:preview.notificationTitle')}</p>
          <p className="text-[11px] text-dark-400">{t('landing:preview.notificationText')}</p>
        </div>
      </div>
    </div>
  );
};
